import { useCallback } from 'react';

import {
  PropertyValue,
  useGetConsumerPropertiesByCause,
  useUpdateBackerConsumerProperties,
} from '@givebacks/api/backer';
import { useShowCauseW_Selector } from '@givebacks/api/core';
import { useForm, useFormyValidator } from '@givebacks/formy';
import { getErrorMessage_v2 } from 'core/Utils';
import {
  AspectRatio,
  ButtonMantine,
  ImageMantine,
  Modal,
  ModalProps,
  Stack,
  ThemeIcon,
  notifications,
} from 'ui';
import { UseFormReturnType } from 'ui/Form';
import { useDisclosure } from 'ui/Hooks';
import { IconChevronRight } from 'ui/Icons';

import { ClickableOptionsCard } from '../../../../Cards/ClickableOptionsCard/ClickableOptionsCard';
import { PropertyFieldValue } from '../../../../molecules/inputs/PropertyFieldValue/PropertyFieldValue';

interface AdditionalInformationModalProps extends ModalProps {
  causeId: string;
}

type AdditionalInformationForm = {
  properties: Record<string, string>;
};

const SaveChangesButton = ({ causeId, onClose }: { causeId: string; onClose?: () => void }) => {
  const { mutate: updateBackerProperties, isPending } = useUpdateBackerConsumerProperties({
    causeId,
  });
  const { useGetForm } = useFormyValidator<{
    propertiesForm: UseFormReturnType<AdditionalInformationForm>;
  }>();

  const form = useGetForm('propertiesForm');

  const handleSave = useCallback(() => {
    if (form.isDirty()) {
      updateBackerProperties(
        {
          properties: form?.values?.properties,
        },
        {
          onSuccess: () => {
            notifications.show({
              message: 'Changes saved',
              variant: 'success',
            });

            onClose?.();
          },
          onError: (error) => {
            notifications.show({
              message: getErrorMessage_v2(error, 'Failed to save changes'),
              variant: 'error',
            });
          },
        }
      );
    }
  }, [form, updateBackerProperties, onClose]);

  return (
    <Modal.AffixedFooter>
      <ButtonMantine fullWidth loading={isPending} onClick={handleSave}>
        Save Changes
      </ButtonMantine>
    </Modal.AffixedFooter>
  );
};

const AdditionalInformationForm = ({ properties }: { properties?: PropertyValue[] }) => {
  const form = useForm<AdditionalInformationForm>(
    {
      initialValues: {
        properties:
          Object.fromEntries(
            properties?.map((property) => [property.property_id ?? '', property.value ?? '']) ?? []
          ) ?? {},
      },
    },
    {
      id: 'propertiesForm',
    }
  );

  if (!properties) {
    return null;
  }

  return (
    <Stack>
      {properties.map((property) => (
        <PropertyFieldValue
          key={property.uuid}
          property={property.property}
          label={property.property?.name ?? ''}
          {...form.getInputProps(`properties.${property.property_id}`)}
        />
      ))}
    </Stack>
  );
};

const AdditionalInformationModal = ({
  causeId,
  opened,
  onClose,
}: AdditionalInformationModalProps) => {
  const { data: properties } = useGetConsumerPropertiesByCause({ causeId });

  return (
    <Modal opened={opened} onClose={onClose} title="Additional Information">
      <Stack gap="lg">
        <ClickableOptionsCard>
          <ClickableCauseCard causeId={causeId} clickable={false} />
        </ClickableOptionsCard>

        <AdditionalInformationForm properties={properties as PropertyValue[]} />

        <SaveChangesButton causeId={causeId} onClose={onClose} />
      </Stack>
    </Modal>
  );
};

const ClickableCauseCard = ({
  causeId,
  clickable = true,
}: {
  causeId: string;
  clickable?: boolean;
}) => {
  const { data: cause } = useShowCauseW_Selector({ uuid: causeId });
  const [opened, handlers] = useDisclosure(false);

  return (
    <>
      <ClickableOptionsCard.Row
        title={cause?.name}
        leftSection={
          <AspectRatio ratio={1} w={48} h={48}>
            <ImageMantine src={cause?.logo?.url} radius={12} />
          </AspectRatio>
        }
        rightSection={
          clickable ? (
            <ThemeIcon size="sm" variant="transparent" c="gray.4">
              <IconChevronRight size={16} />
            </ThemeIcon>
          ) : (
            <></>
          )
        }
        onClick={clickable ? handlers.open : undefined}
      />
      <AdditionalInformationModal causeId={causeId} opened={opened} onClose={handlers.close} />
    </>
  );
};

export {
  AdditionalInformationForm,
  AdditionalInformationModal,
  ClickableCauseCard,
  SaveChangesButton,
};
